/**
 * Design System — MídiaTV
 *
 * Fonte única de verdade para espaçamentos, alturas, radius, sombras e
 * constantes de layout. Importar aqui ao invés de hardcodar valores nas telas.
 *
 * Uso:
 *   import { ds } from '../theme/ds'
 *   <Box sx={{ borderRadius: ds.radius.card, height: ds.height.headerDesktop }} />
 */

// ─── Radius ───────────────────────────────────────────────────────────────────
// Auditoria encontrou 8 valores diferentes. Consolidados em 6 semânticos.
export const radius = {
  xs:     6,   // badges de tipo, tooltips
  sm:     8,   // botões, inputs, chips pequenos
  md:     10,  // ícones de card, itens de lista
  card:   14,  // cards de seção, cards de playlist/produto
  cardLg: 18,  // StatCards (KPI)
  menu:   12,  // menus contextuais, dropdowns
  pill:   9999,// badges de status (sempre circular)
  full:   '50%',
} as const

// ─── Spacing ──────────────────────────────────────────────────────────────────
// MUI usa múltiplos de 8px. 1 = 8px, 2 = 16px, 3 = 24px.
export const spacing = {
  pagePaddingMobile:  2,    // 16px — px de páginas no mobile
  pagePaddingDesktop: 3,    // 24px — px de páginas no desktop
  cardPaddingMobile:  1.75, // 14px — p interno de cards no mobile
  cardPaddingDesktop: 2.25, // 18px — p interno de cards no desktop
  sectionGapMobile:   1.25, // 10px — gap entre seções no mobile
  sectionGapDesktop:  1.75, // 14px — gap entre seções no desktop
} as const

// ─── Alturas de layout ────────────────────────────────────────────────────────
export const height = {
  // Headers de página
  headerMobile:  48,  // todas as telas mobile
  headerDesktop: 56,  // todas as telas desktop
  sidebarLogo:   64,  // logo no topo da sidebar

  // Botões (por contexto)
  btnXs:        28,  // chips clicáveis, badges de filtro
  btnSm:        32,  // botões compactos em header mobile (Salvar, Excluir)
  btnMd:        36,  // botões normais em header desktop
  btnLg:        48,  // botões em formulários (acessibilidade: min 44px)
  btnMobile:    52,  // botões primários mobile (toque confortável)

  // DataGrid
  rowCompact:   44,  // linhas DataGrid mobile
  rowNormal:    52,  // linhas DataGrid desktop
  headerRow:    36,  // header da coluna DataGrid
  headerRowLg:  42,  // header da coluna DataGrid (quando mais espaço necessário)
  toolbar:      48,  // toolbar do DataGrid (busca)

  // Inputs
  inputMd:      44,  // inputs em formulários
  inputSm:      36,  // inputs compactos em toolbars

  // Ícones de card KPI
  kpiIcon:      44,  // StatCard ícone (reduzido de 52px)
  kpiIconSm:    40,  // StatCard ícone mobile horizontal
} as const

// ─── Tipografia ───────────────────────────────────────────────────────────────
export const font = {
  // Tamanhos frequentes
  pageTitleMobile:  17,  // título de página no mobile
  pageTitleDesktop: 20,  // h5 nos headers desktop
  cardTitle:        14,  // título de card/seção
  itemTitle:        13,  // nome de item em lista
  itemSub:          11,  // subtítulo/metadado de item
  badge:            11,  // texto de badge/chip
  label:            12,  // labels de coluna, headers de tabela
  price:            13,  // preço de produto
  kpiValue:         26,  // número em StatCard (reduzido de 28px)
  kpiLabel:         13,  // label do StatCard

  // Pesos
  normal:  400,
  medium:  500,
  semibold:600,
  bold:    700,
} as const

// ─── Sombras ──────────────────────────────────────────────────────────────────
export const shadow = {
  none:   'none',
  card:   '0 1px 4px rgba(0,0,0,0.06)',           // cards light
  cardDark: 'none',                                // cards dark (borda faz o trabalho)
  fab:    '0 6px 20px rgba(20,184,160,0.4)',       // FAB teal
  fabHover:'0 8px 28px rgba(20,184,160,0.5)',      // FAB hover
  menu:   '0 8px 24px rgba(0,0,0,0.12)',           // menus light
  menuDark:'0 8px 32px rgba(0,0,0,0.5)',           // menus dark
  kpi:    '0 0 0 1px rgba(20,184,160,0.2), inset 0 1px 0 rgba(20,184,160,0.1)',
  modal:  '0 24px 48px rgba(0,0,0,0.18)',          // modais
} as const

// ─── Gradientes de marca ──────────────────────────────────────────────────────
export const gradient = {
  teal:     'linear-gradient(135deg, #14b8a0 0%, #0d9488 100%)',
  tealHover:'linear-gradient(135deg, #0d9488 0%, #0a7a70 100%)',
  tealKpi:  'linear-gradient(145deg, #0f2e35 0%, #0c2129 100%)',   // dark
  tealKpiL: 'linear-gradient(145deg, #e6f7f5 0%, #d0f0ec 100%)',   // light
} as const

// ─── Animações ────────────────────────────────────────────────────────────────
export const motion = {
  fast:    '0.15s ease',
  normal:  '0.2s ease',
  slow:    '0.3s ease',
} as const

// ─── Z-index ──────────────────────────────────────────────────────────────────
export const zIndex = {
  fab:     1200,
  header:  100,
  overlay: 50,
} as const

// ─── Breakpoints (referência) ─────────────────────────────────────────────────
// MUI padrão: xs<600, sm<900, md<1200, lg<1536
// Nosso breakpoint mobile: theme.breakpoints.down('sm') = < 600px
export const bp = {
  mobile: 600,  // < 600px = mobile layout
} as const

// ─── Export único ─────────────────────────────────────────────────────────────
export const ds = { radius, spacing, height, font, shadow, gradient, motion, zIndex, bp } as const
export type DS = typeof ds
